import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Link2, Search, FileText, Briefcase, Building2, Newspaper, Star } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_CONFIG = {
  news: {
    label: 'News',
    icon: Newspaper,
    fetchUrl: (search) => `/api/intelligence/news?page_size=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    getName: (item) => item.title_en || item.title?.en || item.title || 'Untitled',
    getSubtitle: (item) => item.country || '',
  },
  tenders: {
    label: 'Tenders',
    icon: Briefcase,
    fetchUrl: (search) => `/api/intelligence/tenders?page_size=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    getName: (item) => (typeof item.title === 'object' ? item.title?.en || item.title?.ar : item.title) || 'Untitled',
    getSubtitle: (item) => item.country || '',
  },
  projects: {
    label: 'Projects',
    icon: Building2,
    fetchUrl: (search) => `/api/intelligence/projects?page_size=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    getName: (item) => item.canonical_name || item.name || 'Untitled',
    getSubtitle: (item) => item.country || '',
  },
  companies: {
    label: 'Companies',
    icon: Building2,
    fetchUrl: (search) => `/api/intelligence/companies?page_size=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    getName: (item) => item.canonical_name || item.name || 'Untitled',
    getSubtitle: (item) => item.country || '',
  },
  master_projects: {
    label: 'Master Projects',
    icon: Star,
    fetchUrl: (search) => `/api/intelligence/projects?is_master_project=true&page_size=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    getName: (item) => item.canonical_name || item.name || 'Untitled',
    getSubtitle: (item) => item.country || '',
  },
};

const BASE_LINKABLE_TYPES = ['news', 'tenders', 'projects', 'companies'];

/**
 * ManageLinksDialog — allows admin/creator to link any entity to news, tenders, projects, companies.
 * @param {boolean} open
 * @param {function} onOpenChange
 * @param {string} fromType — 'news' | 'tenders' | 'projects' | 'companies'
 * @param {string} fromId — the entity's _id
 * @param {string} fromTitle — display name of the entity
 * @param {object} currentLinks — { news: [], tenders: [], projects: [], companies: [] }
 * @param {string} token
 * @param {function} onSuccess — called after save
 */
const ManageLinksDialog = ({
  open, onOpenChange,
  fromType, fromId, fromTitle,
  currentLinks = {},
  token,
  onSuccess,
}) => {
  // For projects: include master_projects tab in addition to regular types
  const otherTypes = fromType === 'projects'
    ? [...BASE_LINKABLE_TYPES.filter(t => t !== fromType), 'master_projects']
    : BASE_LINKABLE_TYPES.filter(t => t !== fromType);
  const [activeTab, setActiveTab] = useState(otherTypes[0]);
  const [items, setItems] = useState({});           // { type: [...] }
  const [loading, setLoading] = useState({});       // { type: bool }
  const [search, setSearch] = useState({});         // { type: string }
  const [selected, setSelected] = useState({});     // { type: Set<id> }
  const [saving, setSaving] = useState(false);

  // Init selected sets from currentLinks on open
  useEffect(() => {
    if (!open) return;
    const init = {};
    otherTypes.forEach(t => { init[t] = new Set(currentLinks[t] || []); });
    setSelected(init);
    setSearch({});
    // Pre-fetch active tab
    fetchItems(activeTab, '');
  }, [open, fromId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchItems = useCallback(async (type, searchVal) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const cfg = ENTITY_CONFIG[type];
      const res = await fetch(`${API_URL}${cfg.fetchUrl(searchVal)}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setItems(prev => ({ ...prev, [type]: data.items || data.data || [] }));
      }
    } catch (e) {
      console.error('fetchItems error', e);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  }, [token]);

  const handleTabChange = (type) => {
    setActiveTab(type);
    if (!items[type]) fetchItems(type, search[type] || '');
  };

  const handleSearch = (type, val) => {
    setSearch(prev => ({ ...prev, [type]: val }));
    fetchItems(type, val);
  };

  const toggleItem = (type, id) => {
    setSelected(prev => {
      const s = new Set(prev[type] || []);
      s.has(id) ? s.delete(id) : s.add(id);
      return { ...prev, [type]: s };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const type of otherTypes) {
        const initial = new Set(currentLinks[type] || []);
        const current = selected[type] || new Set();

        const toAdd    = [...current].filter(id => !initial.has(id));
        const toRemove = [...initial].filter(id => !current.has(id));

        if (type === 'master_projects') {
          // Use dedicated master project link endpoints
          for (const masterProjectId of toAdd) {
            const res = await fetch(`${API_URL}/api/admin/projects/link-master`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ project_id: fromId, master_project_id: masterProjectId }),
            });
            if (!res.ok) {
              const err = await res.json();
              throw new Error(err.detail || 'Failed to link Master Project');
            }
          }
          for (const masterProjectId of toRemove) {
            const res = await fetch(`${API_URL}/api/admin/projects/link-master`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ project_id: fromId, master_project_id: masterProjectId }),
            });
            if (!res.ok) {
              const err = await res.json();
              throw new Error(err.detail || 'Failed to unlink Master Project');
            }
          }
        } else {
          for (const toId of toAdd) {
            const res = await fetch(`${API_URL}/api/admin/link`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ from_type: fromType, from_id: fromId, to_type: type, to_id: toId }),
            });
            if (!res.ok) {
              const err = await res.json();
              throw new Error(err.detail || `Failed to link ${type}`);
            }
          }

          for (const toId of toRemove) {
            const res = await fetch(`${API_URL}/api/admin/link`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ from_type: fromType, from_id: fromId, to_type: type, to_id: toId }),
            });
            if (!res.ok) {
              const err = await res.json();
              throw new Error(err.detail || `Failed to unlink ${type}`);
            }
          }
        }
      }

      toast.success('Links updated successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update links');
    } finally {
      setSaving(false);
    }
  };

  const countChanges = () => {
    let n = 0;
    for (const type of otherTypes) {
      const initial = new Set(currentLinks[type] || []);
      const current = selected[type] || new Set();
      n += [...current].filter(id => !initial.has(id)).length;
      n += [...initial].filter(id => !current.has(id)).length;
    }
    return n;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Link2 className="w-4 h-4" />
            Manage Links
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
            <span className="font-medium">{fromTitle}</span>
          </p>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b px-6 shrink-0">
          {otherTypes.map(type => {
            const cfg = ENTITY_CONFIG[type];
            const Icon = cfg.icon;
            const count = (selected[type] || new Set()).size;
            return (
              <button
                key={type}
                onClick={() => handleTabChange(type)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === type
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`link-tab-${type}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cfg.label}
                {count > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 ml-0.5">{count}</Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* Search + list */}
        <div className="flex flex-col flex-1 overflow-hidden px-6 py-3 gap-3">
          <div className="relative shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder={`Search ${ENTITY_CONFIG[activeTab]?.label.toLowerCase()}...`}
              value={search[activeTab] || ''}
              onChange={(e) => handleSearch(activeTab, e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="link-search-input"
            />
          </div>

          <ScrollArea className="flex-1 border rounded-md" style={{ height: 280 }}>
            {loading[activeTab] ? (
              <div className="flex items-center justify-center h-full py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : !items[activeTab]?.length ? (
              <div className="flex items-center justify-center h-full py-8 text-sm text-muted-foreground">
                No {ENTITY_CONFIG[activeTab]?.label.toLowerCase()} found
              </div>
            ) : (
              <div className="divide-y">
                {(items[activeTab] || [])
                  .filter(item => item._id !== fromId)
                  .map(item => {
                    const cfg = ENTITY_CONFIG[activeTab];
                    const isChecked = (selected[activeTab] || new Set()).has(item._id);
                    return (
                      <label
                        key={item._id}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 cursor-pointer"
                        data-testid={`link-item-${item._id}`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleItem(activeTab, item._id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{cfg.getName(item)}</p>
                          {cfg.getSubtitle(item) && (
                            <p className="text-xs text-muted-foreground">{cfg.getSubtitle(item)}</p>
                          )}
                        </div>
                      </label>
                    );
                  })}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <span className="text-xs text-muted-foreground mr-auto">
            {countChanges()} unsaved change{countChanges() !== 1 ? 's' : ''}
          </span>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-links-btn">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving...</> : 'Save Links'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManageLinksDialog;
